import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Bell,
  Clock,
  IndianRupee,
  Edit,
  Loader2,
  CheckCircle
} from "lucide-react";

export const SIPRemindersPage = () => {
  const { token } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    fund_name: "",
    scheme_code: "",
    amount: "",
    sip_date: "5",
    reminder_days_before: "2"
  });

  const fetchReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sip-reminders`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const createReminder = async () => {
    if (!newReminder.fund_name || !newReminder.amount || !newReminder.sip_date) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await axios.post(`${API}/sip-reminders`, {
        fund_name: newReminder.fund_name,
        scheme_code: newReminder.scheme_code || null,
        amount: parseFloat(newReminder.amount),
        sip_date: parseInt(newReminder.sip_date),
        reminder_days_before: parseInt(newReminder.reminder_days_before)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("SIP reminder created!");
      setIsDialogOpen(false);
      setNewReminder({ fund_name: "", scheme_code: "", amount: "", sip_date: "5", reminder_days_before: "2" });
      fetchReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create reminder");
    }
  };

  const toggleReminder = async (reminderId, isActive) => {
    try {
      await axios.put(`${API}/sip-reminders/${reminderId}`, {
        is_active: !isActive
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(isActive ? "Reminder paused" : "Reminder activated");
      fetchReminders();
    } catch (error) {
      toast.error("Failed to update reminder");
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/sip-reminders/${reminderId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Reminder deleted");
      setReminders(reminders.filter(r => r.reminder_id !== reminderId));
    } catch (error) {
      toast.error("Failed to delete reminder");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getNextSIPDate = (sipDate) => {
    const today = new Date();
    let nextDate = new Date(today.getFullYear(), today.getMonth(), sipDate);
    if (today.getDate() > sipDate) {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
    return nextDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const sipDates = Array.from({ length: 28 }, (_, i) => i + 1);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="sip-reminders-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              SIP <span className="gold-text">Reminders</span>
            </h1>
            <p className="text-muted-foreground">Never miss your SIP dates with email reminders</p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-reminder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add SIP Reminder
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Create SIP Reminder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Fund Name *</Label>
                  <Input
                    placeholder="e.g., HDFC Mid-Cap Opportunities Fund"
                    value={newReminder.fund_name}
                    onChange={(e) => setNewReminder({ ...newReminder, fund_name: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="sip-fund-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Scheme Code (Optional)</Label>
                  <Input
                    placeholder="e.g., 120503"
                    value={newReminder.scheme_code}
                    onChange={(e) => setNewReminder({ ...newReminder, scheme_code: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label>SIP Amount (₹) *</Label>
                  <Input
                    type="number"
                    placeholder="5000"
                    value={newReminder.amount}
                    onChange={(e) => setNewReminder({ ...newReminder, amount: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="sip-amount"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SIP Date *</Label>
                    <Select 
                      value={newReminder.sip_date} 
                      onValueChange={(v) => setNewReminder({ ...newReminder, sip_date: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-white/10" data-testid="sip-date-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-60">
                        {sipDates.map((d) => (
                          <SelectItem key={d} value={d.toString()}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Remind Before</Label>
                    <Select 
                      value={newReminder.reminder_days_before} 
                      onValueChange={(v) => setNewReminder({ ...newReminder, reminder_days_before: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="1">1 day before</SelectItem>
                        <SelectItem value="2">2 days before</SelectItem>
                        <SelectItem value="3">3 days before</SelectItem>
                        <SelectItem value="5">5 days before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={createReminder} className="w-full btn-primary" data-testid="submit-reminder-btn">
                  Create Reminder
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Card */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="glass-card">
            <CardContent className="p-6 text-center">
              <Calendar className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-2xl font-bold">{reminders.length}</p>
              <p className="text-sm text-muted-foreground">Total SIPs</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="text-2xl font-bold">{reminders.filter(r => r.is_active).length}</p>
              <p className="text-sm text-muted-foreground">Active Reminders</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6 text-center">
              <IndianRupee className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-2xl font-bold">
                {formatCurrency(reminders.reduce((sum, r) => sum + (r.is_active ? r.amount : 0), 0))}
              </p>
              <p className="text-sm text-muted-foreground">Monthly SIP</p>
            </CardContent>
          </Card>
        </div>

        {/* Reminders List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Your SIP Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : reminders.length > 0 ? (
              <div className="space-y-4">
                {reminders.map((reminder, index) => (
                  <div
                    key={reminder.reminder_id || index}
                    className={`p-4 rounded-xl ${reminder.is_active ? 'bg-secondary/30' : 'bg-secondary/10 opacity-60'}`}
                    data-testid={`reminder-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium">{reminder.fund_name}</h3>
                          <Badge className={reminder.is_active ? "bg-green-500/20 text-green-500" : "bg-muted text-muted-foreground"}>
                            {reminder.is_active ? "Active" : "Paused"}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <IndianRupee className="w-4 h-4" />
                            {formatCurrency(reminder.amount)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {reminder.sip_date}{reminder.sip_date === 1 ? 'st' : reminder.sip_date === 2 ? 'nd' : reminder.sip_date === 3 ? 'rd' : 'th'} of every month
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Next: {getNextSIPDate(reminder.sip_date)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={reminder.is_active}
                          onCheckedChange={() => toggleReminder(reminder.reminder_id, reminder.is_active)}
                          data-testid={`toggle-reminder-${index}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => deleteReminder(reminder.reminder_id)}
                          data-testid={`delete-reminder-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No SIP Reminders</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Add your SIPs to receive email reminders before due dates
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="glass-card">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              About SIP Reminders
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Get email reminders before your SIP debit date</li>
              <li>• Ensure sufficient balance in your bank account</li>
              <li>• Track all your SIPs in one place</li>
              <li>• Pause or resume reminders anytime</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
